import React, {
  useState,
  useEffect
} from 'react';

import {
  Box,
  Flex,
  Modal,
  Table,
  Button,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';
import {
  types,
  FileSelect
} from '@sopranodesign/pulse-files';

import {
  createResource,
  deleteResource,
  saveResourceTags,
  createResourceTag,
  fetchResourceTags,
  verifyResourceAdd,
  fetchResourceAvailableTags
} from '../api';
import { StateLevel } from '.';
import WarningMessageBody from './WarningMessageBody';
import { useTagManager } from './useTagManager';
import TagModal from './TagModal';

const Resource = () => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [upload, setUpload] = useState(false);
  const [modal, setModal] = useState(null);
  const [isTagsModalVisible, setIsTagsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [selectedTagsId, setSelectedTagsId] = useState(null);

  const order = useOrderableTableReducer({ id: 'resources-document' });
  const pagination = usePaginationReducer({ id: 'resources-document' });

  const { error, isLoading, data: resources } = useQuery({
    queryKey: ['generative-ai/resource', { ...pagination.params, ...order.params }],
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const { data: availableTagsData } = useQuery({
    queryKey: ['generative-ai/resource/groups/available'],
    queryFn: fetchResourceAvailableTags,
    staleTime: 5 * 60 * 1000,
    enabled: canViewTag
  });

  const { tags, selectedTags, setSelectedTags, handleTagChange, setTags } = useTagManager({
    scope: 'generative_ai_files',
    createTagFn: createResourceTag
  });

  const refetchResources = () => {
    queryClient.invalidateQueries({ queryKey: ['generative-ai/resource', { ...pagination.params, ...order.params }] });
  };

  useEffect(() => {
    const pending = resources?.data?.some(r => ['Pending', 'Deleting', 'Preparing'].includes(r.status));
    setShouldRefetch(pending);
  }, [resources]);

  const handleNewResource = async (file) => {
    try {
      const errorMsg = `This document "${file.name}" is already part of your generative AI knowledge. To replace it, delete the original first.`;

      const localExists = resources?.data?.some(r => r.name === file.name);
      if (localExists) {
        showError(errorMsg);
        return setUpload(false);
      }

      const verifyResponse = await verifyResourceAdd(file.id);
      if (verifyResponse?.data?.exists) {
        showError(errorMsg);
        return setUpload(false);
      }

      await createResource(file);
      refetchResources();
      showSuccess('Resource added successfully!');
      setUpload(false);
    } catch (err) {
      showError(`Failed to add resource: ${err.message}`);
    }
  };

  const openTagsModal = async (resource) => {
    try {
      const fetchedTags = availableTagsData?.data?.data ?? [];
      setTags(fetchedTags);
      const assignedTagsResponse = await fetchResourceTags(resource.id);
      const currentTags = assignedTagsResponse.data.data ?? [];
      setSelectedTags(currentTags.map(tag => tag.id));
      setSelectedTagsId(resource.id);
      setIsTagsModalVisible(true);
    } catch (err) {
      showError(`Failed to fetch tags: ${err.message}`);
    }
  };

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading resource...</Paragraph>
      </Flex>
    );
  }

  if (error) {
    return <p>Error fetching resources: {error.message}</p>;
  }

  const meta = resources?.meta ?? {};
  const data = resources?.data ?? [];

  const modalConfigs = {
    deleteResource: (resource) => ({
      header: 'Confirm Delete',
      body: <WarningMessageBody
        contentHeader={`Are you sure you want to delete the resource "${resource.name}"?`}
        contentBody="All synced data will be permanently removed."
        warningText="This action cannot be undone."
      />,
      confirmText: 'Delete',
      handleConfirm: async () => {
        setLoading(true);
        try {
          const response = await deleteResource(resource.id);
          if (response.status === 202) {
            showSuccess(`Resource "${resource.name}" delete requested`);
          } else {
            showError(`Failed to delete resource: Unexpected status ${response.status}`);
          }
        } catch (err) {
          showError(`Failed to delete resource: ${err.message}`);
        } finally {
          setLoading(false);
          setModal(null);
          refetchResources();
        }
      }
    })
  };

  return (
    <Flex col>
      {upload && (
        <FileSelect
          types={[types.pdf, types.document, types.spreadsheet]}
          handleFileSelect={handleNewResource}
          handleCancel={() => setUpload(false)}
        />
      )}

      {data.length === 0 ? (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>No documents have been added yet</Paragraph>
            <PermissionFilter can="edit generative ai resource">
              <PermissionFilter can="view files">
                <PermissionFilter can="view file sources">
                  <Button
                    icon="plus"
                    onClick={() => setUpload(true)}
                  >
                    Add Document
                  </Button>
                </PermissionFilter>
              </PermissionFilter>
            </PermissionFilter>
          </Flex>
        </Box>
      ) : (
        <>
          <Flex border xScroll>
            <Table loading={isLoading}>
              <TableHead>
                <OrderableTableRow
                  rows={[
                    { label: 'Name', sort: 'name' },
                    { label: 'Type', sort: 'type' },
                    { label: 'Status', sort: 'status' },
                    ...(canViewTag ? [{ label: 'Tags' }] : []),
                    ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                  ]}
                  {...order.props}
                />
              </TableHead>
              <TableBody>
                {data.map((resource, key) => (
                  <TableRow key={key}>
                    <TableCell>{resource.name}</TableCell>
                    <TableCell>{resource.type}</TableCell>
                    <TableCell>
                      <Flex center>
                        <StateLevel level={resource.status} />
                        <span>{resource.status}</span>
                      </Flex>
                    </TableCell>
                    <PermissionFilter can="view generative ai tag">
                      <TableCell>
                        {resource.groups?.length > 0
                          ? resource.groups.map(group => group.name).join(', ')
                          : 'No Tags'}
                      </TableCell>
                    </PermissionFilter>
                    {((canViewTag && canEditTag) || canEditResource) && (
                      <TableActions>
                        <PermissionFilter can="view generative ai tag">
                          <PermissionFilter can="edit generative ai tag">
                            {resource.status === 'Success' && (
                              <Button
                                icon="tag"
                                onClick={() => openTagsModal(resource)}
                              >
                                Tags
                              </Button>
                            )}
                          </PermissionFilter>
                        </PermissionFilter>
                        <PermissionFilter can="edit generative ai resource">
                          <Button
                            variant="secondary"
                            icon="trash"
                            colour="danger"
                            onClick={() => setModal(modalConfigs.deleteResource(resource))}
                          >
                            Delete
                          </Button>
                        </PermissionFilter>
                      </TableActions>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Flex>
          {meta && <Pagination pagination={meta} {...pagination.props} />}
          <PermissionFilter can="edit generative ai resource">
            <PermissionFilter can="view files">
              <PermissionFilter can="view file sources">
                <Button
                  mt
                  mlAuto
                  icon="plus"
                  onClick={() => setUpload(true)}
                >
                  Add Document
                </Button>
              </PermissionFilter>
            </PermissionFilter>
          </PermissionFilter>
        </>
      )}

      <TagModal
        isVisible={isTagsModalVisible}
        header="Document Resource Tags"
        onCancel={() => {
          setIsTagsModalVisible(false);
          setSelectedTags([]);
        }}
        onConfirm={async () => {
          try {
            await saveResourceTags({ resourceId: selectedTagsId, groupIds: selectedTags });
            setIsTagsModalVisible(false);
            setSelectedTags([]);
            showSuccess('Tags assigned successfully!');
            refetchResources();
          } catch (err) {
            showError(`Failed to assign tags: ${err.message}`);
          }
        }}
        tags={tags}
        selectedTags={selectedTags}
        handleTagChange={handleTagChange}
      />

      {modal && (
        <Modal
          header={modal.header}
          body={modal.body}
          confirmText={modal.confirmText}
          cancelText="Cancel"
          handleCancel={() => setModal(null)}
          handleConfirm={modal.handleConfirm}
          loading={loading}
        />
      )}
    </Flex>
  );
};

export default Resource;